import { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { getSessionUser } from "@/lib/auth-session";
import { toast } from "sonner";
import { useLocale } from "@/hooks/useLocale";

export interface FeatureFlags {
  build_mode: boolean;
  tts_elevenlabs: boolean;
  tts_inworld: boolean;
  fandom_search: boolean;
  wiki_search: boolean;
  serp_search: boolean;
  proxy_requests: boolean;
  image_generation: boolean;
}

export type LockedFlags = Partial<Record<keyof FeatureFlags, boolean>>;

const ALL_ENABLED: FeatureFlags = {
  build_mode: true,
  tts_elevenlabs: true,
  tts_inworld: true,
  fandom_search: true,
  wiki_search: true,
  serp_search: true,
  proxy_requests: true,
  image_generation: true,
};

export const FEATURE_FLAG_LABELS: Record<keyof FeatureFlags, string> = {
  build_mode: "Build Mode (World Builder)",
  tts_elevenlabs: "TTS — ElevenLabs",
  tts_inworld: "TTS — Inworld",
  fandom_search: "Fandom Wiki Search",
  wiki_search: "Wikipedia Search",
  serp_search: "Google Search (SERP)",
  proxy_requests: "Web Proxy Requests",
  image_generation: "Image Generation",
};

export const FEATURE_FLAG_DESCRIPTIONS: Record<keyof FeatureFlags, string> = {
  build_mode: "Access to the World Builder editor",
  tts_elevenlabs: "Text-to-speech via ElevenLabs voices",
  tts_inworld: "Text-to-speech via Inworld voices",
  fandom_search: "Search Fandom wikis for reference material",
  wiki_search: "Search Wikipedia for lore and facts",
  serp_search: "Google search for web references",
  proxy_requests: "Fetch external web pages",
  image_generation: "AI image generation for entities & scenes",
};

/**
 * Read & update feature flags for a given user (or current user if no userId).
 */
export function useFeatureFlags(userId?: string, adminMode?: boolean) {
  const { t } = useLocale();
  const [flags, setFlags] = useState<FeatureFlags>(ALL_ENABLED);
  const [lockedFlags, setLockedFlags] = useState<LockedFlags>({});
  const [loading, setLoading] = useState(true);
  const isMounted = useRef(true);
  // StrictMode-safe: reset on mount so the second (real) mount can update state.
  // Without this, the cleanup from StrictMode's simulated first mount leaves
  // `isMounted.current = false`, and the `load()` finally block never runs
  // `setLoading(false)` — the world page stays stuck on the loading spinner.
  useEffect(() => {
    isMounted.current = true;
    return () => { isMounted.current = false; };
  }, []);

  const load = useCallback(async () => {
    try {
      let targetId = userId;
      if (!targetId) {
        const user = await getSessionUser();
        targetId = user?.id;
      }
      if (!targetId) { if (isMounted.current) setLoading(false); return; }

      const { data, error } = await supabase
        .from("user_feature_flags" as any)
        .select("*")
        .eq("user_id", targetId)
        .maybeSingle();

      if (error) {
        console.error("Failed to load feature flags:", error);
        if (isMounted.current) setLoading(false);
        return;
      }

      if (data && isMounted.current) {
        const d = data as any;
        setFlags({
          build_mode: d.build_mode ?? true,
          tts_elevenlabs: d.tts_elevenlabs ?? true,
          tts_inworld: d.tts_inworld ?? true,
          fandom_search: d.fandom_search ?? true,
          wiki_search: d.wiki_search ?? true,
          serp_search: d.serp_search ?? true,
          proxy_requests: d.proxy_requests ?? true,
          image_generation: d.image_generation ?? true,
        });
        setLockedFlags((d.locked_flags as LockedFlags) ?? {});
      }
    } catch (err) {
      if (isMounted.current) console.error("Feature flags error:", err);
    } finally {
      if (isMounted.current) setLoading(false);
    }
  }, [userId]);

  useEffect(() => { load(); }, [load]);

  const updateFlag = useCallback(async (key: keyof FeatureFlags, value: boolean) => {
    // If locked and not admin, block change
    if (!adminMode && lockedFlags[key]) {
      toast.error(t.ff_locked);
      return;
    }

    let targetId = userId;
    if (!targetId) {
      const user = await getSessionUser();
      targetId = user?.id;
    }
    if (!targetId) return;

    const { error } = await supabase
      .from("user_feature_flags" as any)
      .upsert({
        user_id: targetId,
        [key]: value,
        updated_at: new Date().toISOString(),
      } as any, { onConflict: "user_id" });

    if (error) {
      toast.error(t.ff_update_failed);
      console.error(error);
      return;
    }

    setFlags(prev => ({ ...prev, [key]: value }));
  }, [userId, adminMode, lockedFlags, t]);

  const updateLock = useCallback(async (key: keyof FeatureFlags, locked: boolean) => {
    let targetId = userId;
    if (!targetId) {
      const user = await getSessionUser();
      targetId = user?.id;
    }
    if (!targetId) return;

    const newLocked = { ...lockedFlags, [key]: locked };

    const { error } = await supabase
      .from("user_feature_flags" as any)
      .upsert({
        user_id: targetId,
        locked_flags: newLocked,
        updated_at: new Date().toISOString(),
      } as any, { onConflict: "user_id" });

    if (error) {
      toast.error(t.ff_lock_failed);
      console.error(error);
      return;
    }

    setLockedFlags(newLocked);
  }, [userId, lockedFlags, t]);

  const updateFlags = useCallback(async (updates: Partial<FeatureFlags>) => {
    let targetId = userId;
    if (!targetId) {
      const user = await getSessionUser();
      targetId = user?.id;
    }
    if (!targetId) return;

    const { error } = await supabase
      .from("user_feature_flags" as any)
      .upsert({
        user_id: targetId,
        ...updates,
        updated_at: new Date().toISOString(),
      } as any, { onConflict: "user_id" });

    if (error) {
      toast.error(t.ff_update_flags_failed);
      console.error(error);
      return;
    }

    setFlags(prev => ({ ...prev, ...updates }));
  }, [userId, t]);

  return { flags, lockedFlags, loading, updateFlag, updateLock, updateFlags, reload: load };
}

/**
 * Check if a feature is enabled and show a toast if not.
 */
export function checkFeatureEnabled(flags: FeatureFlags, key: keyof FeatureFlags): boolean {
  if (flags[key]) return true;
  toast.error(`${FEATURE_FLAG_LABELS[key]} is currently disabled for your account. Contact an administrator to enable it.`);
  return false;
}
